import { AsyncThunkAction, createAsyncThunk, Dispatch } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { AddOrUpdateMemberToTagInput } from '@optimus/models';
import * as contentAPI from '../../api/contentAPI';
import { RootState } from '../storeSetup';
import { Content, ContentStatus } from '../../common/models/content/Content';
import { OnboardingForm } from '../../common/models/OnboardingForm';
import { SubmitSurveyFormRequest } from '../../common/models/content/SubmitSurveyFormRequest';
import { FormAnswer } from '../../common/models/FormAnswer';
import { GetContentRequest, GetContentRequestV4 } from '../../common/models/content/GetContentRequest';
import { FollowTopic } from '../../common/models/FollowTopic';
import { TopicsCount } from '../../common/models/content/TopicsCount';
import { Logger } from '../../config/logging/logger';

export interface ResponseError {
  statusCode?: number
  errorMessage?: string
}

export const getPersonalizedContent = createAsyncThunk<
Content[],
{
  memberId: string;
  arn?: string;
  legacy?: boolean;
  promotionStrategy?: string,
  filterStrategy?: string,
  randomSeed?: number
},
{ rejectValue: ResponseError; state: RootState }
>(
  'content/getPersonalizedContent',
  async ({
    memberId,
    arn,
    legacy,
    promotionStrategy,
    filterStrategy,
    randomSeed,
  }, { rejectWithValue }) => {
    try {
      const contentList = await contentAPI.getPersonalizedContent(
        memberId,
        arn,
        legacy,
        promotionStrategy,
        filterStrategy,
        randomSeed,
      );
      return contentList as Content[];
    } catch (err) {
      const responseError: ResponseError = { errorMessage: 'failed to get personalized content' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getContentById = createAsyncThunk<
// Return type of the payload creator
Content,
// First argument to the payload creator
number,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/getContentById',
  async (contentId, { rejectWithValue, getState }) => {
    try {
      const response = await contentAPI.getContentById(contentId);
      return response as Content;
    } catch (err) {
      const statusCode = (err as AxiosError)?.response?.status;

      const responseError: ResponseError = {
        statusCode,
        errorMessage: statusCode === 400 ?
          `Cannot find content with id ${contentId}` :
          `An error occurred while getting content with id ${contentId}`,
      };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getContentByTitle = createAsyncThunk<
// Return type of the payload creator
Content[],
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/getContentByTitle',
  async (title, { rejectWithValue, getState }) => {
    try {
      const response = await contentAPI.getContentByTitle(title);
      return response as Content[];
    } catch (err) {
      const statusCode = (err as AxiosError)?.response?.status;

      const responseError: ResponseError = {
        statusCode,
        errorMessage: statusCode === 400 ?
          `Cannot find content with title "${title}"` :
          `An error occurred while getting content with title "${title}"`,
      };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getContentForm = createAsyncThunk<
// Return type of the payload creator
OnboardingForm,
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/getForm',
  async (formId, { rejectWithValue, getState }) => {
    try {
      const response = await contentAPI.getContentForm(formId);
      return response as OnboardingForm;
    } catch (err) {
      const responseError: ResponseError = { errorMessage: 'failed to get content form' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getContentFormAnswers = createAsyncThunk<
// Return type of the payload creator
FormAnswer[],
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/getFormAnswers',
  async (formId, { rejectWithValue, getState }) => {
    try {
      const { memberSession: { memberId } } = getState().onboarding;
      const response = await contentAPI.getFormAnswers(formId, memberId);
      return response as FormAnswer[];
    } catch (err) {
      const responseError: ResponseError = { errorMessage: 'failed to get content form answers' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const saveContentFormAnswers = createAsyncThunk<
// Return type of the payload creator
FormAnswer[],
// First argument to the payload creator
SubmitSurveyFormRequest,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/saveContentFormAnswers',
  async (request, { rejectWithValue, getState }) => {
    try {
      const response = await contentAPI.saveContentFormAnswers(request);
      return response as FormAnswer[];
    } catch (err: unknown) {
      // TODO: handle request errors appropriately
      const responseError = ((err as AxiosError)?.response?.data ||
          { errorMessage: 'something went wrong' }) as ResponseError;
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getFollowTopics = createAsyncThunk<
// Return type of the payload creator
FollowTopic[],
// First argument to the payload creator
undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/getFollowTopics',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { memberSession: { memberId } } = getState().onboarding;
      const response = await contentAPI.getFollowTopics(memberId);
      return response as FollowTopic[];
    } catch (err) {
      const responseError: ResponseError = { errorMessage: 'failed to get follow topics' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const addFollowTopic = createAsyncThunk<
// Return type of the payload creator
FollowTopic[],
// First argument to the payload creator
AddOrUpdateMemberToTagInput,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/addFollowTopic',
  async (request, { rejectWithValue, getState }) => {
    try {
      const { memberSession: { memberId } } = getState().onboarding;
      await contentAPI.addFollowTopic(memberId, request);
      const updatedFollowTopics = await contentAPI.getFollowTopics(memberId);
      return updatedFollowTopics as FollowTopic[];
    } catch (err) {
      const responseError: ResponseError = { errorMessage: 'failed to add follow topics' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const deleteFollowTopic = createAsyncThunk<
// Return type of the payload creator
FollowTopic[],
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/deleteFollowTopic',
  async (tagId, { rejectWithValue, getState }) => {
    try {
      const { memberSession: { memberId } } = getState().onboarding;
      await contentAPI.deleteFollowTopic(memberId, tagId);
      const updatedFollowTopics = await contentAPI.getFollowTopics(memberId);
      return updatedFollowTopics as FollowTopic[];
    } catch (err) {
      const responseError: ResponseError = { errorMessage: 'failed to delete follow topics' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getTopTopics = createAsyncThunk<
// Return type of the payload creator
TopicsCount[],
// First argument to the payload creator
undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/getTopTopics',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await contentAPI.getTopTopics();
      return response as TopicsCount[];
    } catch (err) {
      const responseError: ResponseError = { errorMessage: 'failed to get top topics' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

function createContentCategoryStatusAsyncThunk(type: string) {
  const logger = Logger.create(type);
  return createAsyncThunk<
  // Return type of the payload creator
  ContentStatus,
  // First argument to the payload creator
  string,
  // Types for ThunkAPI
  {
    rejectValue: ResponseError;
  }>(
    type,
    async (contentIds, { rejectWithValue }) => {
      try {
        const contentStatus = await contentAPI.getContentStatus(contentIds);
        return contentStatus as ContentStatus;
      } catch (err) {
        logger.error(Logger.format(err));
        const responseError: ResponseError = { errorMessage: 'failed to get content status' };
        return rejectWithValue(responseError as ResponseError);
      }
    },
  );
}

export const getTopTradeContentStatus = createContentCategoryStatusAsyncThunk('content/getTopTradeContentStatus');
export const getVideoContentStatus = createContentCategoryStatusAsyncThunk('content/getVideoContentStatus');
export const getArticleContentStatus = createContentCategoryStatusAsyncThunk('content/getArticleContentStatus');
export const getPodcastContentStatus = createContentCategoryStatusAsyncThunk('content/getPodcastContentStatus');
export const getRewardsContentStatus = createContentCategoryStatusAsyncThunk('content/getRewardsContentStatus');
export const getTopic1ContentStatus = createContentCategoryStatusAsyncThunk('content/getTopic1ContentStatus');
export const getTopic2ContentStatus = createContentCategoryStatusAsyncThunk('content/getTopic2ContentStatus');
export const getTopic3ContentStatus = createContentCategoryStatusAsyncThunk('content/getTopic3ContentStatus');
export const getTopic4ContentStatus = createContentCategoryStatusAsyncThunk('content/getTopic4ContentStatus');
export const getTopic5ContentStatus = createContentCategoryStatusAsyncThunk('content/getTopic5ContentStatus');
export const getContentStatus = createContentCategoryStatusAsyncThunk('content/getContentStatus');
export const getFilteredContentStatus = createContentCategoryStatusAsyncThunk('content/getFilteredContentStatus');

export const getContent = createAsyncThunk<
// Return type of the payload creator
Content[],
// First argument to the payload creator
GetContentRequest,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'content/getContent',
  async (request, { rejectWithValue, dispatch, getState }) => {
    const logger = Logger.create('content/getContent');
    try {
      let contentList: Content[];
      if (request.searchTerm || request.topic || request.businessGoal) {
        request.setHasNoResults?.(false);
        contentList = await contentAPI.getSearchContent(request);
        if (contentList?.length === 0 && request.page === 1) { // if initial search fails
          contentList = await contentAPI.getContent(request) as Content[];
          request.setHasNoResults?.(true);
        }
      } else if (request.onlySavedContent) {
        contentList = await contentAPI.getSavedContent(request) as Content[];
      } else if (request.homeFeedPersonalizeDecision?.enabled) {
        // TODO support pagination
        request.setHasNoResults?.(true);
        contentList = await contentAPI.getPersonalizeContent(
          request,
          request.homeFeedPersonalizeDecision,
        );
      } else {
        contentList = await contentAPI.getContent(request) as Content[];
      }

      const contentIds = contentList.map((content) => content.id).join(',');
      if (contentIds) {
        dispatch(getContentStatus(contentIds));
      }
      return contentList;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get content' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

function createContentCategoryAsyncThunk(
  type: string,
  getContentStatusThunk: (arg: string) => AsyncThunkAction<ContentStatus, string, {
    rejectValue: ResponseError;
    state?: unknown;
    dispatch?: Dispatch | undefined;
    extra?: unknown;
    serializedErrorType?: unknown;
    pendingMeta?: unknown;
    fulfilledMeta?: unknown;
    rejectedMeta?: unknown;
  }>,
) {
  return createAsyncThunk<
  // Return type of the payload creator
  Content[],
  // First argument to the payload creator
  GetContentRequestV4,
  // Types for ThunkAPI
  { rejectValue: ResponseError; state: RootState }
  >(
    type,
    async (request, { rejectWithValue, dispatch }) => {
      try {
        const contentList = await contentAPI.getContentByCategory(request) as Content[];
        const contentIds = contentList.map((content) => content.id).join(',');
        if (contentIds) {
          dispatch(getContentStatusThunk(contentIds));
        }
        return contentList;
      } catch (err) {
        const statusCode = (err as AxiosError)?.response?.status;
        const contentQuery = request.businessGoals ||
          request.businessTrades ||
          request.contentOwner ||
          request.contentTopics ||
          request.contentType ||
          request.rewardPartners || '';

        const responseError: ResponseError = {
          statusCode,
          errorMessage: `An error occurred while getting content related to: ${contentQuery}`,
        };
        return rejectWithValue(responseError);
      }
    },
  );
}

export const getContentByTopTrade = createContentCategoryAsyncThunk('content/getContentByTopTrade', getTopTradeContentStatus);
export const getVideoContent = createContentCategoryAsyncThunk('content/getVideoContent', getVideoContentStatus);
export const getArticleContent = createContentCategoryAsyncThunk('content/getArticleContent', getArticleContentStatus);
export const getPodcastContent = createContentCategoryAsyncThunk('content/getPodcastContent', getPodcastContentStatus);
export const getRewardsContent = createContentCategoryAsyncThunk('content/getRewardsContent', getRewardsContentStatus);
export const getTopic1Content = createContentCategoryAsyncThunk('content/getTopic1Content', getTopic1ContentStatus);
export const getTopic2Content = createContentCategoryAsyncThunk('content/getTopic2Content', getTopic2ContentStatus);
export const getTopic3Content = createContentCategoryAsyncThunk('content/getTopic3Content', getTopic3ContentStatus);
export const getTopic4Content = createContentCategoryAsyncThunk('content/getTopic4Content', getTopic4ContentStatus);
export const getTopic5Content = createContentCategoryAsyncThunk('content/getTopic5Content', getTopic5ContentStatus);
export const getFilteredContent = createContentCategoryAsyncThunk('content/getFilteredContent', getFilteredContentStatus);
